import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, MenuItem, Paper, Select, Stack, Typography,} from '@mui/material';
import CNumberField from 'crqlar-react-common/dist/components/CNumberField';
import {SeatConfig, SeatSlotWithPosition} from '../types';
import {getCircleSeatLayout, normalizeAngleDeg, snapAngleDeg,} from '../utils/fabric-factory';
import {FABRIC_DEFAULTS} from '../utils/fabric-defaults';
import {TABLE_CAPACITY_MAX} from '../utils/constants';
import {
    buildCircleArcPath,
    buildDisplayNumberById,
    clampAngleToCircleArc,
    CIRCLE_PRESET_LAYOUTS,
    CirclePreset,
    getCirclePoint,
    getCirclePresetValue,
} from '../utils/table-configurator-utils';

interface Props {
    width: number;
    height: number;
    seatOffset: number;
    tableName: string;
    seats?: SeatConfig;
    visualSlots: SeatSlotWithPosition[];
    onSetAllSeats: (count: number) => void;
    onUpdateCircleLayout: (updates: Required<NonNullable<SeatConfig['circle']>>) => void;
    onUpdateCircleSeatAngle: (slotId: string, angleDeg: number) => void;
}

const TableSettingsSeatCircleConfigurator: React.FC<Props> = ({
                                                                  width,
                                                                  height,
                                                                  seatOffset,
                                                                  tableName,
                                                                  seats,
                                                                  visualSlots,
                                                                  onSetAllSeats,
                                                                  onUpdateCircleLayout,
                                                                  onUpdateCircleSeatAngle,
                                                              }) => {
    const {t} = useTranslation();
    const displayNumberById = useMemo(() => buildDisplayNumberById(visualSlots), [visualSlots]);
    const seatCount = visualSlots.length;
    const padding = seatOffset + 20;
    const svgWidth = width + padding * 2;
    const svgHeight = height + padding * 2;
    const centerX = svgWidth / 2;
    const centerY = svgHeight / 2;
    const radius = Math.min(width, height) / 2;
    const arcRadius = radius + Math.max(6, seatOffset / 2);
    const svgRef = useRef<SVGSVGElement | null>(null);

    const [activePositionSeatId, setActivePositionSeatId] = useState<string | null>(null);
    const [draftAngles, setDraftAngles] = useState<Record<string, number>>({});
    const draftAnglesRef = useRef<Record<string, number>>({});

    const circleLayout = useMemo(() => getCircleSeatLayout(seats), [seats]);
    const showArcGuide = circleLayout.sweepAngleDeg < 360;
    const currentPreset = getCirclePresetValue(circleLayout);

    useEffect(() => {
        draftAnglesRef.current = draftAngles;
    }, [draftAngles]);

    useEffect(() => {
        if (!activePositionSeatId) return;

        const handlePointerMove = (event: PointerEvent) => {
            const svg = svgRef.current;
            if (!svg) return;

            const rect = svg.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return;

            const x = ((event.clientX - rect.left) / rect.width) * svgWidth;
            const y = ((event.clientY - rect.top) / rect.height) * svgHeight;
            const angleDeg = normalizeAngleDeg(
                (Math.atan2(x - centerX, -(y - centerY)) * 180) / Math.PI
            );
            // Keep drag movement aligned to the configured snap step before showing preview.
            const snapped = snapAngleDeg(angleDeg, circleLayout.snapStepDeg);
            const clamped = clampAngleToCircleArc(
                snapped,
                circleLayout.startAngleDeg,
                circleLayout.sweepAngleDeg
            );

            setDraftAngles((current) => ({
                ...current,
                [activePositionSeatId]: clamped,
            }));
        };

        const handlePointerUp = () => {
            const nextAngle = draftAnglesRef.current[activePositionSeatId];
            if (nextAngle != null) {
                // Persist the last snapped preview angle once the drag finishes.
                onUpdateCircleSeatAngle(activePositionSeatId, nextAngle);
            }
            setActivePositionSeatId(null);
            setDraftAngles({});
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [
        activePositionSeatId,
        centerX,
        centerY,
        circleLayout.snapStepDeg,
        onUpdateCircleSeatAngle,
        svgHeight,
        svgWidth,
    ]);

    const previewSlots = useMemo(() => {
        if (Object.keys(draftAngles).length === 0) return visualSlots;

        return visualSlots.map((slot) => {
            const draftAngle = draftAngles[slot.id];
            if (draftAngle == null) return slot;

            // Repaint only the actively dragged seats from draft state without touching persisted data yet.
            const point = getCirclePoint(draftAngle, radius, seatOffset);
            return {
                ...slot,
                angleDeg: draftAngle,
                x: point.x,
                y: point.y,
            };
        });
    }, [draftAngles, radius, seatOffset, visualSlots]);

    return (
        <>
            <Typography variant="subtitle2" gutterBottom>
                {t('Seat Configuration')}
            </Typography>

            <Paper
                variant="outlined"
                sx={{
                    p: 1,
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#fafafa',
                }}
            >
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                    style={{
                        width: '100%',
                        maxWidth: 220,
                        maxHeight: 180,
                        touchAction: activePositionSeatId ? 'none' : 'auto',
                        overflow: 'visible',
                        userSelect: 'none',
                    }}
                >
                    {showArcGuide && (
                        <path
                            d={buildCircleArcPath(
                                centerX,
                                centerY,
                                arcRadius,
                                circleLayout.startAngleDeg,
                                circleLayout.sweepAngleDeg
                            )}
                            fill="none"
                            stroke="rgba(25, 118, 210, 0.5)"
                            strokeDasharray="4 4"
                            strokeWidth={2}
                        />
                    )}

                    <circle
                        cx={centerX}
                        cy={centerY}
                        r={radius}
                        fill={FABRIC_DEFAULTS.fill}
                        stroke={FABRIC_DEFAULTS.stroke}
                        strokeWidth={2}
                    />

                    {previewSlots.map((slot) => {
                        const isActive = activePositionSeatId === slot.id;
                        return (
                            <g
                                key={slot.id}
                                onPointerDown={(event) => {
                                    event.preventDefault();
                                    // Seed drag preview from the seat's current stored angle.
                                    setActivePositionSeatId(slot.id);
                                    setDraftAngles({[slot.id]: slot.angleDeg ?? 0});
                                }}
                                style={{cursor: isActive ? 'grabbing' : 'grab'}}
                            >
                                <circle
                                    cx={centerX + slot.x}
                                    cy={centerY + slot.y}
                                    r={isActive ? 14 : 11}
                                    fill={isActive ? '#1976d2' : FABRIC_DEFAULTS.seat.fillActive}
                                    stroke={isActive ? '#1565c0' : FABRIC_DEFAULTS.seat.strokeActive}
                                    strokeWidth={isActive ? 3 : 2}
                                />
                                <text
                                    x={centerX + slot.x}
                                    y={centerY + slot.y}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fontSize={10}
                                    fontWeight="bold"
                                    fill="#fff"
                                    style={{pointerEvents: 'none', userSelect: 'none'}}
                                >
                                    {displayNumberById.get(slot.id) ?? (slot.priority ?? slot.position) + 1}
                                </text>
                            </g>
                        );
                    })}

                    <text
                        x={centerX}
                        y={centerY - 6}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={12}
                        fontWeight="bold"
                        fill={FABRIC_DEFAULTS.table.textColor}
                        style={{pointerEvents: 'none', userSelect: 'none'}}
                    >
                        {tableName}
                    </text>
                    <text
                        x={centerX}
                        y={centerY + 8}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={10}
                        fontWeight="normal"
                        fill={FABRIC_DEFAULTS.table.textColor}
                        style={{pointerEvents: 'none', userSelect: 'none'}}
                    >
                        ({seatCount})
                    </text>
                </svg>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {t('Drag a seat around the table to set its angle and order. Positions snap to the configured circle step and stay within the selected arc.')}
            </Typography>

            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="subtitle2" sx={{mb: 1.5}}>
                        {t('Seats')}
                    </Typography>
                    <CNumberField
                        size="small"
                        fullWidth
                        value={seatCount}
                        onChange={(value: number | null) => value !== null && value >= 0 && onSetAllSeats(value)}
                        min={0}
                        max={TABLE_CAPACITY_MAX}
                    />
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{mb: 1.5}}>
                        {t('Arc Layout')}
                    </Typography>
                    <Select
                        size="small"
                        fullWidth
                        value={currentPreset}
                        displayEmpty
                        onChange={(event) => {
                            const preset = event.target.value as CirclePreset;

                            // Presets replace both arc start and sweep in one update.
                            onUpdateCircleLayout({
                                ...circleLayout,
                                ...CIRCLE_PRESET_LAYOUTS[preset],
                            });
                        }}
                    >
                        <MenuItem value="" disabled>
                            {t('Select arc layout')}
                        </MenuItem>
                        <MenuItem value="full">{t('Full circle')}</MenuItem>
                        <MenuItem value="top-half">{t('Top half')}</MenuItem>
                        <MenuItem value="right-half">{t('Right half')}</MenuItem>
                        <MenuItem value="bottom-half">{t('Bottom half')}</MenuItem>
                        <MenuItem value="left-half">{t('Left half')}</MenuItem>
                    </Select>
                </Box>
                {!currentPreset && (
                    <Typography variant="caption" color="text.secondary">
                        {t('This table uses an older custom arc. Choose one of the supported layouts to normalize it.')}
                    </Typography>
                )}
            </Stack>
        </>
    );
};

export default TableSettingsSeatCircleConfigurator;
